import axiosInstance from '../lib/axios';

// Task API types based on the cURL examples
export interface CreateTaskRequest {
  title: string;
  description: string;
  projectId: string;
  assignees: string[];  // Employee user IDs
  followers: string[];  // Employee user IDs
  startDate: string;    // YYYY-MM-DD format
  dueDate?: string;     // YYYY-MM-DD format
  priority: 'Low' | 'Medium' | 'High';
  tags?: string[];
  estimateHours: number;
  url?: string;
}

export interface TaskApiResponse {
  _id: string;
  title: string;
  description: string;
  projectId: string;
  project?: string | {
    _id: string;
    projectName: string;
  };
  projectName?: string; // Direct projectName field from API
  assignees: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId?: string;
    profilePicture?: string | null;
  }>;
  followers: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId?: string;
    profilePicture?: string | null;
  }>;
  startDate: string;
  dueDate?: string;
  priority: 'Low' | 'Medium' | 'High';
  tags: string[];
  estimateHours: number;
  url?: string;
  status?: string | {
    _id: string;
    name: string;
    color?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TasksResponse {
  data: TaskApiResponse[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface TaskFilters {
  search?: string;
  projectId?: string;
  status?: string;
  page?: number;
  limit?: number;
}

class TaskService {
  private baseUrl = '/tasks';

  // CREATE new task
  async createTask(taskData: CreateTaskRequest): Promise<TaskApiResponse> {
    try {
      const response = await axiosInstance.post<TaskApiResponse>(this.baseUrl, taskData);
      return response.data;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  }

  // GET all tasks with filters (project, status, search, pagination)
  async getAllTasks(filters: TaskFilters = {}): Promise<TasksResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.projectId) params.append('projectId', filters.projectId);
      if (filters.status) params.append('status', filters.status);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      
      const queryString = params.toString();
      const url = `${this.baseUrl}${queryString ? `?${queryString}` : ''}`;
      
      console.log(`[TaskService] Fetching all tasks with filters:`, filters);
      const response = await axiosInstance.get<TasksResponse>(url);
      console.log(`[TaskService] Response:`, response.data);
      
      // Handle different response formats
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return response.data;
      } else if (Array.isArray(response.data)) {
        // If API returns array directly, wrap it in response format
        return {
          data: response.data,
          total: response.data.length,
          page: filters.page || 1,
          limit: filters.limit || 20,
          hasMore: false
        };
      } else {
        console.warn('[TaskService] Unexpected response format:', response.data);
        return {
          data: [],
          total: 0,
          page: filters.page || 1,
          limit: filters.limit || 20,
          hasMore: false
        };
      }
    } catch (error: any) {
      console.error(`[TaskService] Error fetching all tasks:`, error);
      console.error(`[TaskService] Error details:`, error.response?.data);
      throw error;
    }
  }

  // GET tasks by project ID
  async getTasksByProject(projectId: string): Promise<TaskApiResponse[]> {
    try {
      console.log(`[TaskService] Fetching tasks for project: ${projectId}`);
      const response = await axiosInstance.get(`/projects/${projectId}/tasks`);
      console.log(`[TaskService] Response:`, response.data);
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      } else if (response.data && Array.isArray(response.data.tasks)) {
        return response.data.tasks;
      } else {
        console.warn('[TaskService] Unexpected response format:', response.data);
        return [];
      }
    } catch (error: any) {
      console.error(`[TaskService] Error fetching tasks for project ${projectId}:`, error);
      console.error(`[TaskService] Error details:`, error.response?.data);
      throw error;
    }
  }

  // GET task by ID
  async getTaskById(taskId: string): Promise<TaskApiResponse> {
    try {
      const response = await axiosInstance.get<TaskApiResponse>(`${this.baseUrl}/${taskId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching task ${taskId}:`, error);
      throw error;
    }
  }

  // UPDATE task
  async updateTask(taskId: string, taskData: Partial<CreateTaskRequest>): Promise<TaskApiResponse> {
    try {
      const response = await axiosInstance.patch<TaskApiResponse>(`${this.baseUrl}/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      console.error(`Error updating task ${taskId}:`, error);
      throw error;
    }
  }

  // UPDATE task status
  async updateTaskStatus(taskId: string, status: string): Promise<TaskApiResponse> {
    try {
      console.log(`[TaskService] Updating task ${taskId} status to: ${status}`);
      const response = await axiosInstance.patch<TaskApiResponse>(
        `${this.baseUrl}/${taskId}/status`, 
        { status }
      );
      console.log(`[TaskService] Status update response:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error(`[TaskService] Error updating task status:`, error);
      console.error(`[TaskService] Error details:`, error.response?.data);
      throw error;
    }
  }

  // DELETE task
  async deleteTask(taskId: string): Promise<void> {
    try {
      await axiosInstance.delete(`${this.baseUrl}/${taskId}`);
    } catch (error) {
      console.error(`Error deleting task ${taskId}:`, error);
      throw error;
    }
  }
}

export const taskService = new TaskService();
export default taskService;

